import { useEffect, useState, useMemo } from 'react';
import { getPublicClasses } from '../../services/api';
import { daysOfWeek, timeSlots, formatTime, isHalfHourTime, getBaseHourSlot, getDurationInSlots } from '../../utils/timeUtils';
import { getClassBackgroundColor } from '../../utils/colors';
import PublicClassBlock from './PublicClassBlock';

// Helper function to get ordinal suffix (1st, 2nd, 3rd, etc.)
const getOrdinalSuffix = (day) => {
  if (day > 3 && day < 21) return 'th';
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
};

// Helper function to get current week date range (Monday to Sunday)
const getCurrentWeekRange = () => {
  const today = new Date();
  const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  
  // Calculate days to subtract to get to Monday
  const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  
  const monday = new Date(today);
  monday.setDate(today.getDate() - daysToMonday);
  
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  
  const formatDate = (date) => {
    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    return `${day}${getOrdinalSuffix(day)} ${month}`;
  };
  
  const mondayFormatted = formatDate(monday);
  const sundayFormatted = formatDate(sunday);
  
  // If same month, show: "1st - 7th Dec"
  // If different months, show: "30th Nov - 6th Dec"
  if (monday.getMonth() === sunday.getMonth()) {
    return `${monday.getDate()}${getOrdinalSuffix(monday.getDate())} - ${sunday.getDate()}${getOrdinalSuffix(sunday.getDate())} ${monday.toLocaleDateString('en-US', { month: 'short' })}`;
  } else {
    return `${mondayFormatted} - ${sundayFormatted}`;
  }
};

// Helper function to get date for a specific day of the week (Monday = 0, Sunday = 6)
const getDateForDay = (dayIndex) => {
  const today = new Date();
  const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  
  // Calculate days to subtract to get to Monday
  const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  
  const monday = new Date(today);
  monday.setDate(today.getDate() - daysToMonday);
  
  // Add the day index (0 = Monday, 6 = Sunday)
  const targetDate = new Date(monday);
  targetDate.setDate(monday.getDate() + dayIndex);
  
  return targetDate.getDate(); // Return just the day number (15, 16, etc.)
};

const PublicCalendar = () => {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const weekRange = getCurrentWeekRange();

  useEffect(() => {
    const fetchClasses = async () => {
      try {
        setLoading(true);
        const response = await getPublicClasses();
        setClasses(response.data || []);
        setError(null);
      } catch (err) {
        console.error('Error fetching public classes:', err);
        setError('Failed to load schedule. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchClasses();
  }, []);

  // Get classes for a day
  const getClassesForDay = (day) => {
    return classes.filter((c) => c.day === day);
  };

  // Get classes for a specific day and time slot
  const getClassesForSlot = (day, time) => {
    return classes.filter((c) => {
      if (c.day !== day) return false;
      // Exact match
      if (c.startTime === time) return true;
      // Half-hour classes belong to their base hour slot
      if (isHalfHourTime(c.startTime) && getBaseHourSlot(c.startTime) === time) {
        return true;
      }
      return false;
    });
  };

  // Get classes that span this time slot (but don't start here)
  const getSpanningClasses = (day, time) => {
    return classes.filter((c) => {
      if (c.day !== day) return false;
      
      // Get the base hour slot for the class start time
      const classBaseSlot = isHalfHourTime(c.startTime) ? getBaseHourSlot(c.startTime) : c.startTime;
      if (classBaseSlot === time) return false; // Already handled by getClassesForSlot
      
      const startIndex = timeSlots.indexOf(classBaseSlot);
      if (startIndex === -1) return false;
      
      // Calculate duration in slots (accounting for half-hour offset)
      const durationSlots = c.duration / 60;
      // If class starts at half-hour, it effectively starts 0.5 slots into the base slot
      const effectiveStartIndex = startIndex + (isHalfHourTime(c.startTime) ? 0.5 : 0);
      const endIndex = effectiveStartIndex + durationSlots;
      const currentIndex = timeSlots.indexOf(time);
      
      // Check if this slot is within the class's time range
      return currentIndex >= startIndex && currentIndex < Math.ceil(endIndex);
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading schedule...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-screen overflow-auto bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-white shadow-md flex items-center justify-center border-2 border-orange-200/50 p-2">
              <img 
                src="/l2blogo.png" 
                alt="Learn2Bhangra" 
                className="h-full w-full object-contain rounded-xl"
                onError={(e) => {
                  console.error('Logo failed to load');
                  e.target.style.display = 'none';
                }}
              />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-orange-600">Learn2Bhangra</h1>
              <p className="text-sm text-gray-500">Class Schedule</p>
            </div>
          </div>
        </div>
      </div>

      {/* Calendar Grid - Horizontal Scroll Container */}
      <div className="overflow-x-auto">
        <div className="min-w-full inline-block">
          {/* Header with days */}
          <div className="flex border-b border-gray-200 sticky top-[73px] bg-white z-10 shadow-sm min-w-max">
            {/* Week Date Range in the first cell */}
            <div className="w-24 sm:w-28 md:w-36 flex-shrink-0 border-r border-gray-200 flex flex-col items-center justify-center bg-white px-1 py-2 sm:px-2 sm:py-2.5">
              <div className="text-xs sm:text-sm font-semibold text-gray-500 uppercase tracking-wide mb-0.5 sm:mb-1">
                Week
              </div>
              <div className="text-xs sm:text-sm md:text-base font-bold text-gray-900 text-center leading-tight px-1">
                {weekRange}
              </div>
            </div>
            {daysOfWeek.map((day, index) => {
              const dayDate = getDateForDay(index);
              const dayClasses = getClassesForDay(day);
              
              // Day gradient colors
              const dayGradients = {
                'Monday': 'from-indigo-400 to-indigo-500',
                'Tuesday': 'from-pink-400 to-pink-500',
                'Wednesday': 'from-amber-400 to-amber-500',
                'Thursday': 'from-emerald-400 to-emerald-500',
                'Friday': 'from-blue-400 to-blue-500',
                'Saturday': 'from-violet-400 to-violet-500',
                'Sunday': 'from-rose-400 to-rose-500',
              };
              
              const gradient = dayGradients[day] || 'from-gray-400 to-gray-500';
              
              return (
                <div
                  key={day}
                  className={`flex-1 flex flex-col items-center justify-center border-r border-gray-200 min-w-[140px] sm:min-w-[160px] md:min-w-[180px] py-2 sm:py-2.5 transition-all duration-200 ${
                    day === 'Saturday' || day === 'Sunday' ? 'bg-blue-50/40' : 'bg-white'
                  }`}
                >
                  {/* Day header */}
                  <div className="relative mb-1.5 sm:mb-2">
                    <div 
                      className={`w-10 h-10 sm:w-11 sm:h-11 md:w-12 md:h-12 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center text-white font-semibold text-sm sm:text-base md:text-lg shadow-md`}
                      style={{
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)'
                      }}
                    >
                      {dayDate}
                    </div>
                  </div>
                  
                  {/* Day name */}
                  <div className="text-xs sm:text-sm font-medium text-gray-600 mb-1 sm:mb-1.5 tracking-wide">
                    {day}
                  </div>
                  
                  {/* Show dots for classes */}
                  {dayClasses.length > 0 && (
                    <div className="flex items-center justify-center gap-1 flex-wrap max-w-full px-1">
                      {dayClasses.map((classItem, idx) => {
                        const classColor = getClassBackgroundColor(classItem.type, classItem.ageGroup);
                        return (
                          <div
                            key={classItem.id || idx}
                            className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full"
                            style={{ 
                              backgroundColor: classColor,
                              minWidth: '4px', 
                              minHeight: '4px',
                              border: '1px solid rgba(0, 0, 0, 0.1)',
                              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
                            }}
                          />
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Time slots */}
          <div className="flex min-w-max">
            {/* Time column */}
            <div className="w-24 sm:w-28 md:w-36 flex-shrink-0 border-r border-gray-200 bg-gray-50/30">
              {timeSlots.map((time) => (
                <div
                  key={time}
                  className="h-32 sm:h-36 md:h-40 border-b border-gray-100 flex items-center justify-center text-sm sm:text-base md:text-lg font-medium text-gray-500"
                >
                  {formatTime(time)}
                </div>
              ))}
            </div>

            {/* Calendar grid */}
            <div className="flex-1 flex min-w-max">
              {daysOfWeek.map((day) => (
                <div
                  key={day}
                  className={`flex-1 border-r border-gray-200 min-w-[140px] sm:min-w-[160px] md:min-w-[180px] ${
                    day === 'Saturday' || day === 'Sunday' ? 'bg-blue-50/40' : 'bg-white'
                  }`}
                >
                  {timeSlots.map((time) => {
                    const slotClasses = getClassesForSlot(day, time);
                    const spanningClasses = getSpanningClasses(day, time);
                    
                    return (
                      <div
                        key={`${day}-${time}`}
                        className={`relative h-32 sm:h-36 md:h-40 border-b ${
                          day === 'Saturday' || day === 'Sunday'
                            ? 'border-gray-300'
                            : 'border-gray-100'
                        }`}
                      >
                        {/* Render class blocks that start at this time */}
                        {slotClasses.map((classItem) => (
                          <PublicClassBlock
                            key={classItem.id}
                            classItem={classItem}
                          />
                        ))}

                        {/* Render spanning classes */}
                        {spanningClasses.length > 0 && (() => {
                          const spanningClass = spanningClasses[0];
                          const classBaseSlot = isHalfHourTime(spanningClass.startTime) ? getBaseHourSlot(spanningClass.startTime) : spanningClass.startTime;
                          const startIndex = timeSlots.indexOf(classBaseSlot);
                          const durationSlots = spanningClass.duration / 60;
                          const effectiveStart = startIndex + (isHalfHourTime(spanningClass.startTime) ? 0.5 : 0);
                          const endIndex = effectiveStart + durationSlots;
                          const currentIndex = timeSlots.indexOf(time);
                          
                          const getSlotHeight = () => {
                            const width = window.innerWidth;
                            if (width < 640) return 128;
                            if (width < 768) return 144;
                            return 160;
                          };
                          
                          const baseHeight = getSlotHeight();
                          let spanHeight = baseHeight;
                          let spanTop = 0;
                          
                          if (currentIndex === startIndex && isHalfHourTime(spanningClass.startTime)) {
                            spanTop = baseHeight / 2;
                            if (endIndex <= startIndex + 1) {
                              spanHeight = (endIndex - effectiveStart) * baseHeight;
                            } else {
                              spanHeight = baseHeight / 2;
                            }
                          } else if (currentIndex < Math.floor(endIndex)) {
                            if (currentIndex === Math.floor(endIndex) - 1 && endIndex % 1 !== 0) {
                              spanHeight = (endIndex % 1) * baseHeight;
                            } else {
                              spanHeight = baseHeight;
                            }
                          } else {
                            return null;
                          }
                          
                          return (
                            <PublicClassBlock
                              key={spanningClass.id}
                              classItem={spanningClass}
                              isSpanning={true}
                              spanHeight={spanHeight}
                              spanTop={spanTop}
                            />
                          );
                        })()}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicCalendar;



